import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button, Link } from '@panwds/react-ui'
import UTSGNew from './UTSGNew'
import UTSGTreeDropdown from './UTSGTreeDropdown'
import UAccordionHeader from './UAccordionHeader'
import { Actions, updateStateProps } from '../../actions'
import { CSP_FIELD_NAME, DEFAULT_TENANT_NAME, TSG_FIELD_NAME } from '../common/GlobalConstants'
import ActivationBanner from '../common/ActivationBanner'

import type { VFC } from 'react'
import type { CommonStoreState } from '../../store'
import type { BannerProps } from './UAccordion'
import { ifIpRestricted } from '../../../common/context/IpRestrictContext'
import { ERROR_API_IP_RESTRICTED_TSG } from '../../../common/constants/GlobalConstants'
import { Title, Body, Subtitle } from '@panwds/react-ui'
import ULearnTenants from './ULearnTenants'
import SubscriptionService from '../../../subscription/services/SubscriptionService'
import { isDefaultTsgFeaturesEnabled } from '../common/utils'

type UTSGDataType = {
  title?: string
  subtitle?: string
  state: CommonStoreState
  actions: Actions
  updateTitle: (title: string) => void
  setAccordionOpen: (isOpen: boolean) => void
  setDisable: (disable: boolean) => void
  setEditDisable: (disable: boolean) => void
  setBanner: (banner: BannerProps) => void
  completeAccordion: () => void
}

type TsgBannerProps = {
  show: boolean
  error: boolean
  isSingleTSG: boolean
  multiTSGText: string
  singleTSGText: string
  infoText: string
  errorText: string
  errorCode: string
  accessToSomeTSGsText: string
  showBannerForDayZeroUser: boolean
}

const defaultTsgBannerProps: TsgBannerProps = {
  show: false,
  error: false,
  isSingleTSG: true, //if true, the banner will display singleTSGText else multiTSGText
  singleTSGText: 'We found a tenant account where products have been activated previously for the selected CSP account. You don’t seem to have access to that tenant account. Please contact your tenant administrator to obtain the required privileges to proceed with activation in that tenant.',
  multiTSGText: 'We found multiple tenant accounts where products have been activated previously for the selected CSP account. You don’t seem to have access to any of those tenant accounts. Please contact your tenant administrator to obtain the required privileges to proceed with activation in one of those tenants.',
  infoText: 'If you’d like to activate the product in a new tenant , click ‘Proceed Anyway’. However, be cautioned that the products activated in the other tenant will not be available to the new tenant. If you are trying to activate dependent products, make sure to activate them in the same tenant.',
  errorText: 'We are unable to complete activation at this time. Please try again later.',
  errorCode: '',
  accessToSomeTSGsText: 'There are other tenants associated with the customer support account for which you don’t have access to. Please contact your tenant administrator to obtain the required privileges to proceed with activation in those tenants.',
  showBannerForDayZeroUser: false
}

const UTSGComponent: VFC<UTSGDataType> = (props) => {
  const { title, subtitle, state, updateTitle, setAccordionOpen, setDisable, setEditDisable, setBanner, completeAccordion } = props
  const { lockedInfo, instances, supportAccountId } = state
  const { setValue, register, setError, clearErrors, getValues } = useFormContext()
  const [selectedTsgId, setSelectedTsgId] = useState<string | undefined>()
  const [recipientName, setRecipientName] = useState<string>('')
  const [tsgNameForDayZeroUser, setTsgNameForDayZeroUser] = useState<string>(DEFAULT_TENANT_NAME)
  const [showLearnAboutTenants, setShowLearnAboutTenants] = useState<boolean>(false)
  const [tsgBanner, setTsgBanner] = useState<TsgBannerProps>(defaultTsgBannerProps)
  const [isCspTsgMapLoading, setIsCspTsgMapLoading] = useState<boolean>(false)
  const [hasAccessToSomeTSGs, setHasAccessToSomeTSGs] = useState<boolean>(false)
  const enableDefaultTsgFeatures = isDefaultTsgFeaturesEnabled(state)

  const defaultProps = {
    title: 'Specify the Recipient',
    subtitle: 'This is the tenant where the product will be activated.',
  }
  const errorMessage = 'Specify the Recipient to proceed activation'
  const isDayZeroUser = useMemo(() => {
    return !Boolean(props.state?.tsgs?.children?.length)
  }, [props.state?.tsgs?.children?.length])

  const closeBanner = useCallback(() => {
    clearErrors('TSG_BANNER')
    setTsgBanner((prevState: TsgBannerProps) => {
      return { ...prevState, show: false }
    })
  }, [clearErrors])

  const setBannerError = (errorCode: string) => {
    setTsgBanner((prevState: TsgBannerProps) => {
      return { ...prevState, errorCode, error: true, show: true }
    })
  }

  /**
   * Fetches the CSP to TSG mapping and related data from the backend.
   * Sets state for loading, disables the component, and shows banners as appropriate.
   * On success, re-enables the component.
   * On failure, shows an error banner
   */
  const fetchCspTsgMapping = useCallback(async (supportAccountId: string) => {
    setIsCspTsgMapLoading(true)
    setDisable(true)
    try {
      const { data } = await SubscriptionService.cspTsgMapping.get({ supportAccountId })
      const isSingleTSG = data?.single_tenant ?? false
      const showBanner = data?.tsg_mapping ? !data?.user_access : false //Show TSG banner based on the CSP=>TSG mapping & the user access
      const _tsgNameForDayZeroUser = data?.tsg_mapping ? DEFAULT_TENANT_NAME : getValues(CSP_FIELD_NAME)?.description || DEFAULT_TENANT_NAME
      const showBannerForDayZeroUser = !data?.tsg_mapping
      const _hasAccessToSomeTSGs = data?.tsg_mapping ? data?.access_some_tsgs : false
      setTsgNameForDayZeroUser(_tsgNameForDayZeroUser)
      setHasAccessToSomeTSGs(_hasAccessToSomeTSGs)
      setTsgBanner((prevState: TsgBannerProps) => {
        return { ...prevState, errorCode: '', error: false, show: showBanner, isSingleTSG, showBannerForDayZeroUser }
      })
      if (showBanner) {
        setError('TSG_BANNER', { message: errorMessage })
      }
    }
    catch (err: any) {
      setBannerError(err?.response?.data?.application_code || 'SUB_SERVICE_CM_1001')
    }
    setIsCspTsgMapLoading(false)
    setAccordionOpen(true)
    setDisable(false)
  }, [getValues, setAccordionOpen, setDisable, setError])


  useEffect(() => {
    if (enableDefaultTsgFeatures && supportAccountId && !selectedTsgId) {
      fetchCspTsgMapping(supportAccountId)
    }
  }, [enableDefaultTsgFeatures, fetchCspTsgMapping, selectedTsgId, supportAccountId])

  register(TSG_FIELD_NAME)
  useEffect(() => {
    if (instances.error) {
      // error on fetching instances, need to setError
      let message: string
      if (ifIpRestricted(instances.error)) {
        message = ERROR_API_IP_RESTRICTED_TSG
      }
      else {
        message = instances.error.message ?? String(instances.error)
      }
      setBanner({
        message: (
          <>
            {message}
            <br />
            Please try another Tenant.
          </>
        ),
        display: true,
        appearance: 'error',
      })
      setError(TSG_FIELD_NAME, instances.error)
    }
    else {
      setBanner({
        display: false,
      })
      clearErrors(TSG_FIELD_NAME)
    }
  }, [clearErrors, instances.error, setBanner, setError])

  useEffect(() => {
    if (!selectedTsgId) {
      if (isDayZeroUser) {
        updateTitle(`Recipient: ${recipientName}`)
      }
      return
    }
    const selectedTsg = state.tsgs.get(selectedTsgId)
    updateStateProps({ tsg: selectedTsg })
    updateTitle(`Recipient: ${selectedTsg?.display_name}`)
    setValue(TSG_FIELD_NAME, {
      id: selectedTsgId,
      value: selectedTsg?.display_name
    })
  }, [isDayZeroUser, recipientName, selectedTsgId, setValue, state.tsgs, updateTitle])

  const tsgDisabled = useMemo(() => {
    // disable only if set by lockedInfo, if user only have 1 tsg, don't disable and let user choose
    return Boolean(lockedInfo?.tsg_ids?.length === 1)
  }, [lockedInfo?.tsg_ids?.length])

  useEffect(() => {
    if (tsgDisabled && lockedInfo?.tsg_ids?.length === 1) {
      setSelectedTsgId(lockedInfo.tsg_ids[0])
      setEditDisable(true)
      setAccordionOpen(false)
      completeAccordion()
    }
    else {
      setEditDisable(false)
    }
  }, [completeAccordion, lockedInfo, setAccordionOpen, setEditDisable, tsgDisabled])

  useEffect(() => {
    if (state.supportAccountId) {
      enableDefaultTsgFeatures && isCspTsgMapLoading ? setDisable(true) : setDisable(false)
    }
    else {
      setDisable(true)
    }
  }, [enableDefaultTsgFeatures, isCspTsgMapLoading, setDisable, state.supportAccountId])

  const RecipientHeader = () => (
    <div className='cm-w-full cm-mb-3'>
      <Title level={3} size='xs'>{title || defaultProps.title}</Title>
      <Subtitle>
        <Body addClassName='cm-pr-2' appearance='secondary' as='span'>{subtitle || defaultProps.subtitle}</Body>
        <Link onClick={() => {
          setShowLearnAboutTenants(prev => !prev)
        }}>
          {showLearnAboutTenants ? 'Hide information about tenants' : 'Learn more about tenants'}
        </Link>
      </Subtitle>
    </div>)
  const TSGDropDown = useMemo(() => (
    <>
      <div className='cm-mt-4 cm-w-full tw-space-y-4'>
        {isDayZeroUser ?
          <UTSGNew {...props} setRecipientName={setRecipientName} tsgName={tsgNameForDayZeroUser} showBanner={tsgBanner.showBannerForDayZeroUser}/> :
          <>
            {(hasAccessToSomeTSGs && enableDefaultTsgFeatures) && <ActivationBanner infoMessage={defaultTsgBannerProps.accessToSomeTSGsText} appearance='standard' type='inline' />}
            <UTSGTreeDropdown {...props} selectedTsgId={selectedTsgId} onChangeCallback={(tsgId) => {
              setSelectedTsgId(tsgId)
              setAccordionOpen(false)
              completeAccordion()
            }} />
          </>
        }
      </div>
      {isDayZeroUser && <Button
        addClassName='cm-absolute cm-right-0 cm-top-0'
        appearance='primary'
        onClick={() => {
          setAccordionOpen(false)
          completeAccordion()
        }}
      >Done</Button>}
    </>
  ), [completeAccordion, enableDefaultTsgFeatures, hasAccessToSomeTSGs, isDayZeroUser, props, selectedTsgId, setAccordionOpen, tsgBanner.showBannerForDayZeroUser, tsgNameForDayZeroUser])

  const Messages = useMemo(() => {
    const message = tsgBanner.isSingleTSG ? tsgBanner.singleTSGText : tsgBanner.multiTSGText
    return (
      <>
        {tsgBanner.error ?
          <ActivationBanner infoMessage={`[${tsgBanner.errorCode}] ${tsgBanner.errorText}`} appearance='error' type='inline' /> :
          <div className="tw-space-y-4">
            <ActivationBanner infoMessage={message} appearance='warning' type='inline' />
            <ActivationBanner
              bannerActions={<Button onClick={closeBanner} appearance="secondary" size="sm">Proceed Anyway</Button>}
              appearance="standard"
              type='inline'
              infoMessage={tsgBanner.infoText}
            />
          </div>
        }
      </>
    )
  }, [closeBanner, tsgBanner.error, tsgBanner.errorCode, tsgBanner.errorText, tsgBanner.infoText, tsgBanner.isSingleTSG, tsgBanner.multiTSGText, tsgBanner.singleTSGText])

  return (
    <div>
      {enableDefaultTsgFeatures ?
        <RecipientHeader /> :
        <UAccordionHeader title={title || defaultProps.title} subtitle={subtitle || defaultProps.subtitle} />
      }
      {(enableDefaultTsgFeatures && tsgBanner.show) ? Messages : TSGDropDown}
      {showLearnAboutTenants && <ULearnTenants />}
    </div>
  )
}

export default UTSGComponent
